import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import MissionService from '../../../service/MissionService';
import ConfirmationDialog from '../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import { showToastMessage } from '../../../utils/commonFunctions';
import { xtceMutationErrorMessage } from './xtceErrors';
import './XtceRegistry.scss';

// ─── Types ──────────────────────────────────────────────────────────────────

interface XtceEntry {
    xtceId: string;
    missionId: string;
    type: 'system' | 'custom';
    xtceName: string;
    xtceVersion: string;
    inUse: boolean;
    createdAt?: string;
    updatedAt?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

const XtceRegistry: FC = () => {
    const params = useParams<{ mission_id: string }>();

    const [entries, setEntries] = useState<XtceEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Upload form state
    const [uploadOpen, setUploadOpen] = useState<boolean>(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadName, setUploadName] = useState<string>('');
    const [uploadVersion, setUploadVersion] = useState<string>('1.0');
    const [uploading, setUploading] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Confirm dialogs
    const [activateTarget, setActivateTarget] = useState<XtceEntry | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<XtceEntry | null>(null);
    const [actionLoading, setActionLoading] = useState<boolean>(false);

    // ─── Data ───────────────────────────────────────────────────────────────

    const refetchEntries = useCallback(() => {
        const { mission_id } = params;
        if (!mission_id) return;
        setLoading(true);
        MissionService.getXtceList(mission_id)
            .then((res: any) => {
                const list: XtceEntry[] = res?.data?.data ?? [];
                // Active first, then customs newest-first, then system entries.
                list.sort((a, b) => {
                    if (a.inUse !== b.inUse) return a.inUse ? -1 : 1;
                    if (a.type !== b.type) return a.type === 'custom' ? -1 : 1;
                    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
                });
                setEntries(list);
            })
            .catch((err: any) => {
                showToastMessage('error', err?.response?.data?.message ?? 'Failed to load XTCE registry.');
            })
            .finally(() => setLoading(false));
    }, [params.mission_id]);

    useEffect(() => { refetchEntries(); }, [refetchEntries]);

    // ─── Upload ─────────────────────────────────────────────────────────────

    const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        if (!file) return;
        setUploadFile(file);
        if (!uploadName) {
            setUploadName(file.name.replace(/\.xml$/i, ''));
        }
        event.target.value = '';
    };

    const resetUploadForm = () => {
        setUploadOpen(false);
        setUploadFile(null);
        setUploadName('');
        setUploadVersion('1.0');
    };

    const submitUpload = () => {
        const { mission_id } = params;
        if (!mission_id || !uploadFile || !uploadName.trim() || !uploadVersion.trim()) {
            showToastMessage('info', 'Choose an XML file and provide a name and version.');
            return;
        }
        const formData = new FormData();
        formData.append('xtceName', uploadName.trim());
        formData.append('xtceVersion', uploadVersion.trim());
        formData.append('xtceFile', uploadFile);
        setUploading(true);
        MissionService.uploadXtce(mission_id, formData)
            .then((res: any) => {
                showToastMessage('success', res?.data?.message ?? 'XTCE uploaded.');
                resetUploadForm();
                refetchEntries();
            })
            .catch((err: any) => {
                showToastMessage('error', xtceMutationErrorMessage(err, 'XTCE upload failed.'));
            })
            .finally(() => setUploading(false));
    };

    // ─── Activate / Delete ──────────────────────────────────────────────────

    const confirmActivate = () => {
        const { mission_id } = params;
        if (!mission_id || !activateTarget) return;
        setActionLoading(true);
        MissionService.activateXtce(mission_id, activateTarget.xtceId)
            .then((res: any) => {
                showToastMessage('success', res?.data?.message ?? `'${activateTarget.xtceName}' is now the active XTCE.`);
                setActivateTarget(null);
                refetchEntries();
            })
            .catch((err: any) => {
                showToastMessage('error', xtceMutationErrorMessage(err, 'Activation failed.'));
            })
            .finally(() => setActionLoading(false));
    };

    const confirmDelete = () => {
        const { mission_id } = params;
        if (!mission_id || !deleteTarget) return;
        setActionLoading(true);
        MissionService.deleteXtce(mission_id, deleteTarget.xtceId)
            .then((res: any) => {
                showToastMessage('success', res?.data?.message ?? `'${deleteTarget.xtceName}' deleted.`);
                setDeleteTarget(null);
                refetchEntries();
            })
            .catch((err: any) => {
                showToastMessage('error', xtceMutationErrorMessage(err, 'Delete failed.'));
            })
            .finally(() => setActionLoading(false));
    };

    // ─── Render helpers ─────────────────────────────────────────────────────

    const formatDate = (iso?: string) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '—' : d.toLocaleString();
    };

    return (
        <div className="xr-container">
            <div className="xr-header">
                <div className="xr-header-titles">
                    <span className="xr-title">XTCE Registry</span>
                    <span className="xr-subtitle">
                        TC/TM schema definitions for this mission — upload a custom XTCE and activate it to make it the schema new scenarios use.
                    </span>
                </div>
                <div className="xr-header-actions">
                    <button
                        id="xr_upload_btn"
                        className="xr-btn xr-btn-primary"
                        onClick={() => setUploadOpen(open => !open)}
                    >
                        <i className="fe fe-upload me-1" /> Upload XTCE
                    </button>
                </div>
            </div>

            {uploadOpen && (
                <div className="xr-upload-panel">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xml,application/xml,text/xml"
                        hidden
                        onChange={onFilePicked}
                    />
                    <button
                        className="xr-btn xr-btn-secondary"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <i className="fe fe-file me-1" />
                        {uploadFile ? uploadFile.name : 'Choose XML file'}
                    </button>
                    <input
                        className="xr-input"
                        placeholder="Name"
                        value={uploadName}
                        onChange={event => setUploadName(event.target.value)}
                    />
                    <input
                        className="xr-input xr-input-version"
                        placeholder="Version"
                        value={uploadVersion}
                        onChange={event => setUploadVersion(event.target.value)}
                    />
                    <button
                        className="xr-btn xr-btn-primary"
                        disabled={uploading || !uploadFile}
                        onClick={submitUpload}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <button className="xr-btn xr-btn-ghost" disabled={uploading} onClick={resetUploadForm}>
                        Cancel
                    </button>
                </div>
            )}

            <div className="xr-panel">
                <div className="xr-table-header">
                    <div className="xr-th xr-col-name">Name</div>
                    <div className="xr-th xr-col-version">Version</div>
                    <div className="xr-th xr-col-type">Type</div>
                    <div className="xr-th xr-col-status">Status</div>
                    <div className="xr-th xr-col-updated">Updated</div>
                    <div className="xr-th xr-col-actions">Actions</div>
                </div>
                <div className="xr-table-body">
                    {loading &&
                        [0, 1, 2].map(index => (
                            <div className="xr-table-row xr-shimmer" key={`shimmer-${index}`}>
                                <div className="xr-td xr-col-name"><span className="xr-shimmer-bar" /></div>
                                <div className="xr-td xr-col-version"><span className="xr-shimmer-bar" /></div>
                                <div className="xr-td xr-col-type"><span className="xr-shimmer-bar" /></div>
                                <div className="xr-td xr-col-status"><span className="xr-shimmer-bar" /></div>
                                <div className="xr-td xr-col-updated"><span className="xr-shimmer-bar" /></div>
                                <div className="xr-td xr-col-actions" />
                            </div>
                        ))}

                    {!loading && entries.length === 0 && (
                        <div className="xr-empty">
                            No XTCE files registered for this mission yet. Upload one to get started.
                        </div>
                    )}

                    {!loading && entries.map(entry => (
                        <div className="xr-table-row" key={entry.xtceId}>
                            <div className="xr-td xr-col-name text-truncate" title={entry.xtceName}>
                                {entry.xtceName}
                            </div>
                            <div className="xr-td xr-col-version">{entry.xtceVersion}</div>
                            <div className="xr-td xr-col-type">
                                <span className={`xr-badge xr-badge-${entry.type}`}>{entry.type}</span>
                            </div>
                            <div className="xr-td xr-col-status">
                                {entry.inUse
                                    ? <span className="xr-badge xr-badge-active">Active</span>
                                    : <span className="xr-badge xr-badge-inactive">Inactive</span>}
                            </div>
                            <div className="xr-td xr-col-updated">{formatDate(entry.updatedAt ?? entry.createdAt)}</div>
                            <div className="xr-td xr-col-actions">
                                {!entry.inUse && (
                                    <CustomToolTip title="Make this the active XTCE for the mission" placement="top">
                                        <button
                                            className="xr-btn xr-btn-small xr-btn-secondary"
                                            onClick={() => setActivateTarget(entry)}
                                        >
                                            Activate
                                        </button>
                                    </CustomToolTip>
                                )}
                                {entry.type === 'custom' && !entry.inUse && (
                                    <CustomToolTip title="Delete this XTCE entry" placement="top">
                                        <button
                                            className="xr-btn xr-btn-small xr-btn-danger"
                                            onClick={() => setDeleteTarget(entry)}
                                        >
                                            <i className="fe fe-trash-2" />
                                        </button>
                                    </CustomToolTip>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <ConfirmationDialog
                type="warning"
                open={!!activateTarget}
                title="Activate XTCE?"
                description={`'${activateTarget?.xtceName ?? ''}' (v${activateTarget?.xtceVersion ?? ''}) will become the active TC/TM schema for this mission. New scenarios will use it; running scenarios keep the schema they started with.`}
                confirm_button_text="Activate"
                confirm_loading_text="Activating…"
                loading={actionLoading}
                onConfirm={confirmActivate}
                onCancel={() => !actionLoading && setActivateTarget(null)}
            />

            <ConfirmationDialog
                type="delete"
                open={!!deleteTarget}
                title="Delete XTCE?"
                description={`'${deleteTarget?.xtceName ?? ''}' (v${deleteTarget?.xtceVersion ?? ''}) will be permanently removed from the registry.`}
                confirm_button_text="Delete"
                confirm_loading_text="Deleting…"
                loading={actionLoading}
                onConfirm={confirmDelete}
                onCancel={() => !actionLoading && setDeleteTarget(null)}
            />
        </div>
    );
};

export default XtceRegistry;
